import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useGetFormData } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Actions } from 'core-components/library/modalDialog'

import { MDF_ACTIVITIES_TRANSLATION_KEYS } from '../../../../configs/translations'
import { requestMdfActivityApply, selectMdfActivitySavingIds } from '../../../../model'
import { IMdfActivityWithBusinessPriorities } from '../../../../model/types'
import { MDF_ACTIVITY_DETAILS_FORM_KEY } from '../../config'
import { getEmptyMdfActivityOutcomeForm, getInitialFormData, TMdfDetailsForm } from '../../helpers'

const KEYS = MDF_ACTIVITIES_TRANSLATION_KEYS.detailsModal

export interface IMdfActivityDetailsFooterProps {
  activity: IMdfActivityWithBusinessPriorities | null
  fYear?: number
  isReadOnly?: boolean
  onSaved?: () => void
  onClose: () => void
}

export const MdfActivityDetailsFooter = ({
  activity,
  fYear,
  isReadOnly,
  onSaved,
  onClose,
}: IMdfActivityDetailsFooterProps) => {
  const dispatch = useAppDispatch()
  const savingIds = useSelector(selectMdfActivitySavingIds)

  const initialForm = useMemo<TMdfDetailsForm>(
    () => (activity ? getInitialFormData(activity) : getEmptyMdfActivityOutcomeForm()),
    [activity]
  )

  const [{ formData }] = useGetFormData<TMdfDetailsForm>(MDF_ACTIVITY_DETAILS_FORM_KEY, initialForm)

  const handleSave = useCallback(async () => {
    if (!activity || isReadOnly) {
      return
    }

    // The Save button only persists the outcome; the initiative link is saved
    // immediately on change via LinkedInitiativeCell, so preserve it here.
    try {
      await dispatch(
        requestMdfActivityApply({
          activity,
          fYear,
          keyInitiativeId: activity.keyInitiativeId,
          outcome: formData.outcome,
          outcomeValueType: formData.outcomeValueType,
          funnelSizeUsd: formData.funnelSizeUsd,
          leads: formData.leads,
          conversionRate: formData.conversionRate,
          attendance: formData.attendance,
          estimatedSalesUsd: formData.estimatedSalesUsd,
        })
      ).unwrap()

      onSaved?.()
      onClose()
    } catch {
      // Keep the modal open so the user can retry; the error surfaces via state.
    }
  }, [activity, fYear, formData, dispatch, isReadOnly, onSaved, onClose])

  if (!activity) {
    return null
  }

  const isSaving = savingIds.includes(activity.entryId)
  const closeAction = {
    id: 'cancel',
    label: <Translation text={KEYS.cancel} span />,
    variant: 'outlined',
    color: 'grey',
    onClick: onClose,
  } as const
  const saveAction = {
    id: 'save',
    label: <Translation text={KEYS.save} span />,
    variant: 'contained',
    color: 'primary',
    disabled: isSaving,
    onClick: handleSave,
  } as const

  return <Actions items={isReadOnly ? [closeAction] : [closeAction, saveAction]} />
}

MdfActivityDetailsFooter.displayName = 'MdfActivityDetailsFooter'
